import type { CustomEventKey, CustomEventCallback } from '../shared/types/events';

export abstract class BaseEvent {
	#eventTarget: EventTarget;

	/**
	 * Creates an instance of the event handler.
	 * @param {EventTarget} [target=document] - The DOM element on which events are dispatched and listened to. Defaults to `document` for a global event bus.
	 */
	constructor(target: EventTarget = document) {
		this.#eventTarget = target;
	}

	/**
	 * Dispatches a custom event on the specified target.
	 *
	 * @param {CustomEventKey} eventName    - The name of the event to dispatch.
	 * @param {Object}         [payload={}] - The data to be sent with the event.
	 * @return {void}
	 */
	protected dispatch(eventName: CustomEventKey, payload: object = {}): void {
		const event = new CustomEvent(eventName, {
			detail: {
				event: eventName,
				payload,
			},
			bubbles: true,
			composed: true,
		});
		this.#eventTarget.dispatchEvent(event);
	}

	/**
	 * Adds a custom event listener to the specified target.
	 *
	 * @param {CustomEventKey}      eventName - The event to listen for.
	 * @param {CustomEventCallback} callback  - The function to execute when the event occurs.
	 * @return {() => void} A function that, when called, removes the event listener.
	 */
	protected listen(eventName: CustomEventKey, callback: CustomEventCallback): () => void {
		if (typeof callback !== 'function') {
			// eslint-disable-next-line no-console
			console.error('EventHandler Error: The provided callback is not a function.');
			return () => {};
		}

		const wrapper = (event: Event): void => {
			if (event instanceof CustomEvent) {
				callback(event);
			}
		};

		this.#eventTarget.addEventListener(eventName, wrapper);

		return () => {
			this.#eventTarget.removeEventListener(eventName, wrapper);
		};
	}
}
